import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const ease = [0.16, 1, 0.3, 1];

function getSessionId() {
  let id = sessionStorage.getItem("viraai-chat-session");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("viraai-chat-session", id);
  }
  return id;
}

const WELCOME = {
  role: "assistant",
  content:
    "Hi, ich bin Vira — deine Kurs-Assistentin. Frag mich alles zur AI Agentur Methode: Preis, Inhalte, für wen der Kurs geeignet ist oder wie schnell du starten kannst.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (sessionStorage.getItem("viraai-chat-autoopen")) return;
    const t = setTimeout(() => {
      sessionStorage.setItem("viraai-chat-autoopen", "1");
      setOpen((o) => o || true);
    }, 10000);
    return () => clearTimeout(t);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          const data = JSON.parse(payload);
          const chunk = data.delta ?? data.error ?? "";
          if (chunk) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                content: copy[copy.length - 1].content + chunk,
              };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Entschuldige, da ist etwas schiefgelaufen. Versuch es bitte gleich nochmal.",
        };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        onClick={() => {
          sessionStorage.setItem("viraai-chat-autoopen", "1");
          setOpen(!open);
        }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.8, duration: 0.4, ease }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-20 md:bottom-5 left-4 md:left-5 z-[65] rounded-full flex items-center justify-center"
        style={{
          width: 52,
          height: 52,
          background: "#C9A84C",
          boxShadow: "0 8px 30px rgba(201,168,76,0.35)",
        }}
        aria-label={open ? "Chat schließen" : "Chat mit Vira öffnen"}
        data-testid="chat-widget-button"
      >
        {open ? (
          <X className="w-5 h-5 text-[#0A0A0A]" />
        ) : (
          <MessageCircle className="w-5 h-5 text-[#0A0A0A]" />
        )}
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease }}
            className="fixed bottom-36 md:bottom-20 left-4 md:left-5 z-[65] flex flex-col rounded-2xl overflow-hidden w-[calc(100vw-2rem)] max-w-sm"
            style={{
              height: "min(480px, 65vh)",
              background: "rgba(17,17,17,0.98)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(201,168,76,0.25)",
              boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
            }}
            data-testid="chat-widget-panel"
          >
            {/* Header */}
            <div
              className="flex items-center gap-3 px-5 py-4 shrink-0"
              style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center font-black text-sm shrink-0"
                style={{ background: "#C9A84C", color: "#0A0A0A" }}
              >
                V
              </div>
              <div className="min-w-0">
                <p className="text-[#F5F5F7] font-bold text-sm leading-tight">Vira</p>
                <p className="text-[#86868b] text-xs">Deine Kurs-Assistentin</p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
                    style={
                      m.role === "user"
                        ? { background: "#C9A84C", color: "#0A0A0A", borderBottomRightRadius: 6 }
                        : { background: "rgba(255,255,255,0.06)", color: "#F0EDE8", borderBottomLeftRadius: 6 }
                    }
                    data-testid={`chat-message-${m.role}-${i}`}
                  >
                    {m.content || (
                      <span className="inline-flex gap-1 items-center py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] animate-bounce" style={{ animationDelay: "300ms" }} />
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div
              className="flex items-center gap-2 px-4 py-3 shrink-0"
              style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
            >
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                maxLength={500}
                placeholder="Deine Frage zum Kurs…"
                className="flex-1 bg-transparent text-[#F0EDE8] text-sm placeholder-[#86868b] outline-none min-w-0"
                data-testid="chat-input"
              />
              <button
                onClick={send}
                disabled={loading || !input.trim()}
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-opacity disabled:opacity-30"
                style={{ background: "#C9A84C" }}
                aria-label="Senden"
                data-testid="chat-send-button"
              >
                <Send className="w-4 h-4 text-[#0A0A0A]" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
